import { useEffect, useRef, useState } from 'react';
import { supportRequest, SupportError, SupportStep } from '../../services/supportBotApi';
import './SupportChatbot.css';
// Only terminal answers offer resolution feedback and an optional support ticket.
import SupportResolution from './SupportResolution';
import { TicketCategory } from '../../services/supportTicketApi';

const copy = {
  en: { title: 'Emasajid support', open: 'Open support assistant', close: 'Close support assistant', intro: 'How can we help?',
    description: 'Choose a topic and we will guide you through the next steps.', start: 'Start conversation', restart: 'Start again',
    busy: 'Please wait…', privacy: 'Do not share personal information, passwords or verification codes.', ticketPrivacy: 'Do not share passwords or verification codes.',
    unavailable: 'We could not connect to support. Please try starting a new conversation.', expired: 'This session has expired. Start again to continue.',
    stale: 'This conversation is out of date. Start again to continue.', limited: 'Too many requests. Please wait before starting again.',
    wait: 'seconds remaining', invalid: 'That option is no longer available. Start again to continue.' },
  ar: { title: 'مساعد منصة المساجد', open: 'فتح مساعد الدعم', close: 'إغلاق مساعد الدعم', intro: 'كيف يمكننا مساعدتك؟',
    description: 'اختر موضوعاً وسنرشدك إلى الخطوات التالية.', start: 'بدء المحادثة', restart: 'البدء من جديد', busy: 'يرجى الانتظار…',
    privacy: 'لا تشارك معلومات شخصية أو كلمات مرور أو رموز تحقق.', ticketPrivacy: 'لا تشارك كلمات مرور أو رموز تحقق.',
    unavailable: 'تعذر الاتصال بخدمة الدعم. يرجى محاولة بدء محادثة جديدة.', expired: 'انتهت صلاحية الجلسة. ابدأ من جديد للمتابعة.',
    stale: 'هذه المحادثة غير محدثة. ابدأ من جديد للمتابعة.', limited: 'طلبات كثيرة. يرجى الانتظار قبل البدء مجدداً.',
    wait: 'ثانية متبقية', invalid: 'لم يعد هذا الخيار متاحاً. ابدأ من جديد للمتابعة.' },
};
type Entry = { role: 'assistant' | 'user'; text: string };

// Only official platform links become clickable. Everything else stays plain text, never HTML.
function Message({ text }: { text: string }) {
  return <>{text.split(/(https:\/\/[^\s]+)/g).map((part, index) => {
    try {
      const url = new URL(part);
      if (url.protocol === 'https:' && url.hostname === 'emasajid.moia.gov.sa' && !url.username && !url.password) {
        return <a key={index} href={url.href} target="_blank" rel="noopener noreferrer" dir="ltr">{part}</a>;
      }
    } catch { /* Regular words are expected here; render them as text below. */ }
    return <span key={index}>{part}</span>;
  })}</>;
}

export default function SupportChatbot({ language, apiBaseUrl }: { language: 'ar' | 'en'; apiBaseUrl: string }) {
  const t = copy[language];
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<SupportStep | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<keyof typeof copy.en | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [ticketCategory, setTicketCategory] = useState<TicketCategory>('other');
  const [ticketLocked, setTicketLocked] = useState(false);
  const [conversation, setConversation] = useState(0);
  const active = useRef<AbortController | null>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const log = useRef<HTMLDivElement>(null);

  // Language changes start a fresh conversation because the API fixes language when a session begins.
  useEffect(() => {
    active.current?.abort(); active.current = null;
    setStep(null); setEntries([]); setError(null); setBusy(false);
    // A new language starts a new conversation and clears only this widget's ticket draft.
    setTicketCategory('other'); setTicketLocked(false);
    return () => { active.current?.abort(); active.current = null; };
  }, [language, apiBaseUrl]);
  useEffect(() => { if (open) closeButton.current?.focus(); }, [open]);
  useEffect(() => { if (log.current) log.current.scrollTop = log.current.scrollHeight; }, [entries, busy, open]);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(n => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  useEffect(() => {
    // A completed guide no longer sends selections; keep its ticket form available after chat expiry.
    if (!step || step.isComplete) return;
    const timer = window.setTimeout(() => { setStep(null); setError('expired'); }, Math.max(0, Date.parse(step.expiresAt) - Date.now()));
    return () => window.clearTimeout(timer);
  }, [step]);

  function close() { setOpen(false); launcher.current?.focus(); }
  async function send(option?: { id: string; label: string }) {
    // This immediate guard stops rapid double-clicks before React has rendered disabled buttons.
    if (active.current || cooldown || ticketLocked) return;
    if (option && (!step || Date.parse(step.expiresAt) <= Date.now())) { setStep(null); setError('expired'); return; }
    const controller = new AbortController(); active.current = controller;
    setBusy(true); setError(null);
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const next = await supportRequest(apiBaseUrl, language, controller.signal, option ? step! : undefined, option?.id);
      if (active.current !== controller) return;
      setStep(next);
      // Suggest the topic the visitor chose, without changing any server-controlled transitions.
      if (!option) { setTicketCategory('other'); setConversation(value => value + 1); }
      else if (step?.version === 0) {
        setTicketCategory(['otp', 'donation', 'mosque'].includes(option.id) ? option.id as TicketCategory : 'other');
      }
      setEntries(previous => option ? [...previous, { role: 'user', text: option.label }, { role: 'assistant', text: next.message }]
        : [{ role: 'assistant', text: next.message }]);
    } catch (failure) {
      if (active.current !== controller) return;
      const status = failure instanceof SupportError ? failure.status : 0;
      setError(status === 401 ? 'expired' : status === 409 ? 'stale' : status === 429 ? 'limited' : status === 400 ? 'invalid' : 'unavailable');
      if (failure instanceof SupportError && status === 429) setCooldown(failure.retryAfter);
      // A timeout may happen after the server advances; restarting is safer than replaying a selection.
      setStep(null);
    } finally {
      window.clearTimeout(timeout);
      if (active.current === controller) { active.current = null; setBusy(false); }
    }
  }
  return <div className="support-chat" dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language}>
    {/* Hiding instead of unmounting preserves the ticket retry key if the visitor closes the panel. */}
    <section hidden={!open} id="support-chat-panel" className="support-chat__panel" role="dialog" aria-modal="false"
      aria-labelledby="support-chat-title" onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); close(); } }}>
      <header className="support-chat__header"><div><strong id="support-chat-title">{t.title}</strong><p>{t.description}</p></div>
        <button type="button" ref={closeButton} onClick={close} aria-label={t.close}>×</button></header>
      <div className="support-chat__log" ref={log} role="log" aria-live="polite" aria-relevant="additions text">
        {!entries.length && <div className="support-chat__welcome"><h2>{t.intro}</h2><p>{t.description}</p></div>}
        {entries.map((entry, index) => <div key={index} className={`support-chat__message support-chat__message--${entry.role}`}><Message text={entry.text} /></div>)}
        {busy && <p role="status">{t.busy}</p>}
      </div>
      <div className="support-chat__actions" aria-busy={busy}>
        {error && <p role="alert">{t[error]}</p>}
        {cooldown > 0 && <p>{cooldown} {t.wait}</p>}
        {!error && step?.options.map(option => <button type="button" key={option.id} disabled={busy} onClick={() => send(option)}>{option.label}</button>)}
        {/* Yes/No is local feedback. A ticket is created only after the visitor submits its form. */}
        {step?.isComplete && !error && <SupportResolution key={conversation} language={language}
          apiBaseUrl={apiBaseUrl} category={ticketCategory} onLockChange={setTicketLocked} />}
        <button type="button" className="support-chat__restart" disabled={busy || cooldown > 0 || ticketLocked} onClick={() => send()}>
          {entries.length || error ? t.restart : t.start}
        </button>
      </div>
      <p className="support-chat__privacy">{step?.isComplete ? t.ticketPrivacy : t.privacy}</p>
    </section>
    <button type="button" ref={launcher} className="support-chat__launcher" aria-expanded={open} aria-controls="support-chat-panel"
      aria-label={open ? t.close : t.open} onClick={() => open ? close() : setOpen(true)}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M20 11a8 8 0 0 1-8 8H5l-4 3V11a9 9 0 0 1 19 0Z"/><path d="M6 10h9M6 14h6"/></svg>
      <span>{t.title}</span>
    </button>
  </div>;
}


